import React, { useEffect, useRef, useState } from "react";
import { MessageCircle, Send, X } from "lucide-react";
import { api } from "@/lib/api";
import { useSettings } from "@/contexts/SettingsContext";

const ChatWidget = () => {
    const [open, setOpen] = useState(false);
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState("");
    const [busy, setBusy] = useState(false);
    const scrollRef = useRef(null);
    const { settings } = useSettings();
    const rawWa = (settings?.contact?.whatsapp || "").trim();
    const waDigits = rawWa.replace(/[^0-9]/g, "");
    const waUrl = waDigits
        ? `https://wa.me/${waDigits.length === 10 ? "91" + waDigits : waDigits}?text=${encodeURIComponent("Hi Cherishables! I have a question about a custom miniature.")}`
        : "";

    useEffect(() => {
        if (open && messages.length === 0) {
            api.get("/chatbot/greeting").then(({ data }) => {
                setMessages([{ role: "bot", text: data.reply }]);
            }).catch(() => {});
        }
    }, [open, messages.length]);

    useEffect(() => { scrollRef.current?.scrollTo({ top: 99999, behavior: "smooth" }); }, [messages]);

    const send = async () => {
        const text = input.trim();
        if (!text) return;
        setMessages((m) => [...m, { role: "user", text }]);
        setInput("");
        setBusy(true);
        try {
            const { data } = await api.post("/chatbot/message", { message: text });
            setMessages((m) => [...m, { role: "bot", text: data.reply }]);
        } catch {
            setMessages((m) => [...m, { role: "bot", text: "Sorry — I couldn't reach the server. Try again in a moment." }]);
        } finally {
            setBusy(false);
        }
    };

    const renderText = (t) =>
        (t || "").split(/(\/(?:product|shop|track|contact|faq)[^\s]*)/g).map((chunk, i) =>
            chunk.startsWith("/") ? <a key={i} href={chunk} className="underline font-semibold">{chunk}</a> : <span key={i}>{chunk}</span>
        );

    return (
        <>
            {waUrl && (
                <a
                    href={waUrl}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="whatsapp-fab"
                    aria-label="Chat on WhatsApp"
                    className="fixed bottom-5 right-24 z-40 w-14 h-14 rounded-full bg-[#25D366] hover:bg-[#1ebe57] text-white shadow-lg flex items-center justify-center transition-transform hover:scale-105"
                    style={{ boxShadow: "0 8px 24px rgba(37,211,102,0.35)" }}
                >
                    <svg viewBox="0 0 448 512" width="28" height="28" fill="currentColor" aria-hidden="true">
                        <path d="M380.9 97.1C339 55.1 283.2 32 223.9 32c-122.4 0-222 99.6-222 222 0 39.1 10.2 77.3 29.6 111L0 480l117.7-30.9c32.4 17.7 68.9 27 106.1 27h.1c122.3 0 224.1-99.6 224.1-222 0-59.3-25.2-115-67.1-157zm-157 341.6c-33.2 0-65.7-8.9-94-25.7l-6.7-4-69.8 18.3L72 359.2l-4.4-7c-18.5-29.4-28.2-63.3-28.2-98.2 0-101.7 82.8-184.5 184.6-184.5 49.3 0 95.6 19.2 130.4 54.1 34.8 34.9 56.2 81.2 56.1 130.5 0 101.8-84.9 184.6-186.6 184.6zm101.2-138.2c-5.5-2.8-32.8-16.2-37.9-18-5.1-1.9-8.8-2.8-12.5 2.8-3.7 5.6-14.3 18-17.6 21.8-3.2 3.7-6.5 4.2-12 1.4-32.6-16.3-54-29.1-75.5-66-5.7-9.8 5.7-9.1 16.3-30.3 1.8-3.7.9-6.9-.5-9.7-1.4-2.8-12.5-30.1-17.1-41.2-4.5-10.8-9.1-9.3-12.5-9.5-3.2-.2-6.9-.2-10.6-.2-3.7 0-9.7 1.4-14.8 6.9-5.1 5.6-19.4 19-19.4 46.3 0 27.3 19.9 53.7 22.6 57.4 2.8 3.7 39.1 59.7 94.8 83.8 35.2 15.2 49 16.5 66.6 13.9 10.7-1.6 32.8-13.4 37.4-26.4 4.6-13 4.6-24.1 3.2-26.4-1.3-2.5-5-3.9-10.5-6.6z"/>
                    </svg>
                </a>
            )}
            {!open && (
                <button
                    onClick={() => setOpen(true)}
                    data-testid="chat-bubble"
                    className="fixed bottom-5 right-5 z-40 w-14 h-14 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground shadow-lg flex items-center justify-center"
                    aria-label="Open chat"
                >
                    <MessageCircle className="w-6 h-6" />
                </button>
            )}
            {open && (
                <div className="fixed bottom-5 right-5 z-40 w-[92vw] max-w-sm rounded-3xl bg-white shadow-2xl border border-primary/10 flex flex-col overflow-hidden" data-testid="chat-panel" style={{ height: 520 }}>
                    <div className="bg-primary text-primary-foreground px-4 py-3 flex items-center justify-between">
                        <div className="font-display font-semibold">Cherishables Assistant</div>
                        <button onClick={() => setOpen(false)} data-testid="chat-close" className="opacity-80 hover:opacity-100"><X className="w-5 h-5" /></button>
                    </div>
                    <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-4 space-y-2 bg-brand-cream/40">
                        {messages.map((m, i) => (
                            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                                <div data-testid={`chat-msg-${m.role}-${i}`} className={`max-w-[85%] px-3 py-2 rounded-2xl text-sm whitespace-pre-line ${m.role === "user" ? "bg-primary text-primary-foreground" : "bg-white border"}`}>
                                    {renderText(m.text)}
                                </div>
                            </div>
                        ))}
                        {busy && <div className="text-xs text-muted-foreground italic">Assistant is typing…</div>}
                    </div>
                    <div className="border-t p-2 flex gap-2 bg-white">
                        <input
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && send()}
                            data-testid="chat-input"
                            placeholder="Ask a question…"
                            className="flex-1 rounded-full border h-10 px-3 text-sm outline-none focus:ring-2 focus:ring-primary"
                        />
                        <button onClick={send} disabled={busy} data-testid="chat-send" className="w-10 h-10 rounded-full bg-primary text-primary-foreground disabled:opacity-50">
                            <Send className="w-4 h-4 mx-auto" />
                        </button>
                    </div>
                </div>
            )}
        </>
    );
};

export default ChatWidget;
